import type { TournamentStructureValue } from 'constants/play';

/**
 * TradeMagic Play wire shapes (Asana 1220000146037011), mirroring the backend
 * plan's Contract delta sections A to D. Base path /api/v1/play unless noted.
 */

export type TournamentStatus = 'LOBBY' | 'PAIRED' | 'LIVE' | 'COMPLETE';
export type VenueType = 'STORE' | 'ONLINE';
export type RoundPhase = 'SWISS' | 'TOP_CUT';
export type MatchStatus = 'PENDING' | 'REPORTED' | 'DISPUTED';
export type ReportedByRole = 'PLAYER' | 'TO' | 'SYSTEM';
export type PlayViewerRole = 'TO' | 'PLAYER' | 'NONE';
export type OnboardingLevel = 'PLAY' | 'FULL';

export interface PlayTournament {
  id: string;
  inviteCode: string;
  /** Absolute, e.g. https://trademagic.gg/play/ab2c3d */
  inviteUrl: string;
  name: string;
  /** ISO UTC */
  startsAt: string;
  /** IANA zone of the organizer */
  timezone: string;
  venueType: VenueType;
  venueText: string | null;
  /** The stored label, Other included */
  format: string;
  /** Resolved display label (customFormatName when Other) */
  formatLabel: string;
  customFormatName?: string | null;
  structure: TournamentStructureValue;
  topCutSize: 4 | 8 | null;
  swissRounds: number;
  roundMinutes: number;
  decklistRequired: boolean;
  lateRegistration: boolean;
  publicPage: boolean;
  status: TournamentStatus;
  registeredCount: number;
  activeCount: number;
  decklistsIn: number;
  adsEnabled: boolean;
  organizer: { username: string };
  /** Swiss rounds plus top-cut rounds when applicable */
  totalRounds: number;
  /** Server-side Decision 17 rule (LOBBY, or late registration before round 2 starts) */
  registrationOpen: boolean;
  /** recommendedSwissRounds(registeredCount), computed server-side */
  recommendedSwissRounds: number;
  completedAt?: string | null;
  createdAt?: string;
}

export interface PlayViewer {
  role: PlayViewerRole;
  userId?: number | null;
  tournamentPlayerId: string | null;
  /** Logged-in non-participant whose account lacks a first or last name */
  needsName: boolean;
  dropped: boolean;
  /** What the name step shows before the player edits anything */
  prefill?: { username: string | null; firstName: string | null; lastName: string | null } | null;
}

export interface PlayPlayer {
  id: string;
  /** Account players */
  username: string | null;
  /** Walk-ins (no account) */
  displayName: string | null;
  /** TO only */
  firstName?: string | null;
  /** TO only */
  lastName?: string | null;
  dropped: boolean;
  hasDecklist: boolean;
  /** TO only */
  deckName?: string | null;
  /** "W-L-D" */
  record: string;
  joinedAtRound: number;
}

export const playerName = (p: Pick<PlayPlayer, 'username' | 'displayName'>): string => p.username ?? p.displayName ?? 'Player';

export interface MatchPlayerRef {
  tournamentPlayerId: string;
  username: string;
  record: string;
  dropped?: boolean;
}

export interface MatchReport {
  tournamentPlayerId: string;
  gamesA: number;
  gamesB: number;
}

export interface MatchState {
  id: string;
  roundId?: string;
  tableNumber: number | null;
  playerA: MatchPlayerRef;
  playerB: MatchPlayerRef | null;
  status: MatchStatus;
  gamesA: number | null;
  gamesB: number | null;
  reportedByRole: ReportedByRole | null;
  reportedById?: string | null;
  /** TO and the two players only. While DISPUTED gamesA and gamesB are null and this carries both claims. */
  reports?: MatchReport[];
  openJudgeCall: { id: string; createdAt: string } | null;
  isBye: boolean;
}

export interface RoundState {
  id: string;
  number: number;
  phase: RoundPhase;
  totalRounds: number;
  startedAt: string | null;
  durationSeconds: number;
  pausedAt: string | null;
  pausedTotalSeconds: number;
  /** Computed by the server at serverTime; the client anchors on it */
  remainingSeconds: number;
  matches: MatchState[];
  resultsIn: number;
  resultsTotal: number;
  serverTime: string;
}

export interface RoundSummary {
  id: string;
  number: number;
  phase: RoundPhase;
  startedAt: string | null;
}

export interface StandingRow {
  rank: number;
  tournamentPlayerId: string;
  username: string;
  dropped: boolean;
  wins: number;
  losses: number;
  draws: number;
  points: number;
  /** 0..1 */
  omw: number;
  gw: number;
  ogw: number;
  /** TO only */
  deckName?: string | null;
  isViewer: boolean;
}

export interface JudgeCallState {
  id: string;
  matchId: string;
  roundId?: string;
  tableNumber: number | null;
  calledByUsername: string;
  createdAt: string;
  resolvedAt: string | null;
}

/** GET /tournaments/:code (auth optional). One payload drives every Play screen for a code. */
export interface TournamentState {
  tournament: PlayTournament;
  viewer: PlayViewer;
  players: PlayPlayer[];
  currentRound: RoundState | null;
  rounds: RoundSummary[];
  standings: StandingRow[];
  myMatch: MatchState | null;
  /** TO only: open judge calls */
  judgeCalls?: JudgeCallState[];
  /** TO only */
  missingDecklists?: { tournamentPlayerId: string; username: string }[];
  /** PLAYER viewer only: the caller's submitted decklist, null when none, absent for TO and NONE */
  myDecklist?: PlayDecklist | null;
  serverTime: string;
}

/** GET /tournaments/:id/decklist (own) and /decklist/:pid (TO). { decklist: null } when none. */
export interface PlayDecklist {
  name: string | null;
  rawText: string;
  parsed?: unknown;
  mainCount: number;
  sideCount: number;
  lockedAt: string | null;
  updatedAt?: string;
}

/** GET /tournaments/:code/live (no auth). */
export interface LiveState {
  tournament: Pick<
    PlayTournament,
    'id' | 'inviteCode' | 'name' | 'startsAt' | 'timezone' | 'venueType' | 'venueText' | 'format' | 'formatLabel' | 'customFormatName' | 'structure' | 'swissRounds' | 'status' | 'registeredCount' | 'activeCount'
  >;
  currentRound: RoundState | null;
  standings: StandingRow[];
  adsEnabled: boolean;
  serverTime: string;
}

/** GET /tournaments/mine: { organized, joined, serverTime }, each ordered by startsAt descending. */
export interface TournamentSummary {
  id: string;
  inviteCode: string;
  name: string;
  startsAt: string;
  timezone: string;
  formatLabel: string;
  structure: TournamentStructureValue;
  status: TournamentStatus;
  registeredCount: number;
  activeCount: number;
  currentRoundNumber: number | null;
  swissRounds: number;
  venueType: VenueType;
  venueText: string | null;
  myRole: 'TO' | 'PLAYER';
  myDropped: boolean;
}

export interface MyTournaments {
  organized: TournamentSummary[];
  joined: TournamentSummary[];
  serverTime: string;
}

export interface CreateTournamentBody {
  name: string;
  startsAt: string;
  timezone: string;
  venueType: VenueType;
  venueText: string;
  format: string;
  customFormatName?: string;
  structure: TournamentStructureValue;
  topCutSize: 4 | 8 | null;
  swissRounds: number;
  roundMinutes: number;
  decklistRequired: boolean;
  lateRegistration: boolean;
  publicPage: boolean;
}

export type PatchTournamentBody = Partial<CreateTournamentBody>;

export interface JoinTournamentBody {
  username: string;
  firstName: string;
  lastName: string;
}

export interface DecklistResult {
  mainCount: number;
  sideCount: number;
  name: string | null;
  lockedAt: string | null;
  unresolved: string[];
}

export type DecklistIssue = 'MAIN_UNDER_60' | 'SIDE_OVER_15';

export interface DecklistInvalidDetails {
  mainCount: number;
  sideCount: number;
  unresolved: string[];
  issues: DecklistIssue[];
}

/** GET/PUT /play/notifications. Web push is deferred (ruling E4), so there is no push field. */
export interface PlayNotificationPrefs {
  /** Always false while web push is deferred (ruling E4); nothing renders for it */
  push: boolean;
  discordDm: boolean;
  sms: boolean;
  /** The account has a Discord ID (logged in with Discord at least once) */
  discordLinked: boolean;
  /** False when the server has no Discord app configured: the Discord row hides and the start endpoints answer 503 */
  discordConfigured?: boolean;
  isElite: boolean;
  phoneVerified: boolean;
  /** Ruling E10: the second Discord authorization (server join) has happened */
  discordGuildMember: boolean;
  /** The TradeMagic Discord server invite for THIS environment (dev app vs prod app); never hardcoded */
  inviteUrl: string | null;
  /** Path to navigate to when the user turns Discord DMs on (append returnTo); same value as 409 DISCORD_SERVER_REQUIRED details.startUrl */
  joinServerStartUrl: string;
}

/** 409 DISCORD_SERVER_REQUIRED details */
export interface DiscordServerRequiredDetails {
  startUrl: string;
}

export interface AdMatch {
  card: { name: string; scryfallId: string; imageUrl: string };
  holderUsername: string;
  /** The holder's profile have-list tab */
  tradeUrl: string;
}

export type PlayNotifyEvent = 'ROUND_START' | 'JUDGE_CALL' | 'RESULT_DISPUTED' | 'OPPONENT_DROPPED';

/** Every room payload carries these (backend Contract delta, section C). */
export interface PlayRoomEnvelope {
  tournamentId: string;
  inviteCode: string;
  serverTime: string;
}

/** play:notify on the user's own socket room */
export interface PlayNotifyPayload {
  event: PlayNotifyEvent;
  title: string;
  body: string;
  tournamentId: string;
  inviteCode: string;
  roundId?: string | null;
  matchId?: string | null;
  tableNumber?: number | null;
}

export interface PlayTournamentEvent extends PlayRoomEnvelope {
  status: TournamentStatus;
  registeredCount: number;
  activeCount: number;
  decklistsIn: number;
}

export type PlayRoundEvent = RoundState & Partial<PlayRoomEnvelope>;
export type PlayMatchEvent = MatchState & Partial<PlayRoomEnvelope>;

export interface PlayJudgeEvent extends PlayRoomEnvelope {
  judgeCall: JudgeCallState;
  matchId: string;
  tableNumber: number | null;
  resolved: boolean;
}

export interface PlayPlayersEvent extends PlayRoomEnvelope {
  players: PlayPlayer[];
}

export interface UsernameAvailability {
  available: boolean;
  reason?: 'LENGTH' | 'RESERVED' | 'TAKEN';
}
